import { h } from "preact";
import { useState, useRef, useLayoutEffect } from "preact/hooks";

import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";
import IconOrganizationAvatarPlaceholder from "../Icons/IconOrganizationAvatarPlaceholder";
import IconUserAvatarPlaceholder from "../Icons/IconUserAvatarPlaceholder";

import styles from "./Avatar.module.css";

enum Type {
    User = "user",
    Organization = "organization"
}

enum AccessoryLocation {
    TopLeft = "topLeft",
    TopRight = "topRight",
    BottomRight = "bottomRight",
    BottomLeft = "bottomLeft",
}

interface Props {
    class?: string;
    style?: CSSProperties;
    type?: Type;
    placeholderType?: Type;
    src?: string;
    srcset?: string;
    title?: string;
    highlight?: boolean;
    accessory?: JSX.Element;
    accessoryLocation?: AccessoryLocation;
}

function Avatar({
    class: cssClass,
    style,
    type = Type.User,
    placeholderType = type,
    src,
    srcset,
    title,
    highlight = false,
    accessory,
    accessoryLocation = AccessoryLocation.TopRight
}: Props) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const imageRef = useRef<HTMLImageElement>();
    const placeholderVisible = loading || error;
    const iconPlaceholder = placeholderType === Type.User
        ? <IconUserAvatarPlaceholder/>
        : <IconOrganizationAvatarPlaceholder/>;

    function handleLoaded() {
        setLoading(false);
        setError(false);
    }

    function handleError() {
        setError(true);
    }

    useLayoutEffect(() => {
        if (imageRef.current?.src && imageRef.current.complete) {
            handleLoaded();
        } else {
            setLoading(true);
            setError(false);
        }
    }, [src, srcset]);

    return (
        <div class={classname(styles.avatarContainer, cssClass)}>
            <div
                class={classname(styles.avatar, styles[type], {
                    [styles.placeholderVisible]: placeholderVisible,
                    [styles.highlight]: highlight
                })}
                style={style}
                title={title}>
                <img
                    ref={imageRef}
                    src={src}
                    srcSet={srcset}
                    alt="&#x1f464;"
                    onLoad={handleLoaded}
                    onError={handleError}/>
                {placeholderVisible && iconPlaceholder}
            </div>
            {accessory && (
                <div
                    class={classname(styles.accessory, styles[accessoryLocation])}>
                    {accessory}
                </div>
            )}
        </div>
    );
}

export default Avatar;
export {
    Props as AvatarProps,
    Type as AvatarType,
    AccessoryLocation as AvatarAccessoryLocation
};
